import { useEffect, useState } from "react";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";

import { RoleByIdSelector, RoleSelectorSkeleton } from "@/features/members/components/RoleSelector";
import { UpdateMemberSchema } from "@/features/projects/schemas";
import { updateProjectMember } from "@/features/projects/services/project";
import { ProjectMember } from "@/features/projects/types";
import Error from "@/shared/components/Error";
import { QUERY_EXPIRATIONS, QUERY_KEYS } from "@/shared/lib/constants/queries";
import { getProjectRoles } from "@/features/members/services/role";
import { Role } from "@/features/members/types";
import { AppError } from "@/shared/lib/utils/errors";

interface Props {
  member: ProjectMember;
  projectId: string;
  children: React.ReactNode;
}

export function ChangeRoleDialog({ member, projectId, children }: Props) {
  const [open, setOpen] = useState(false);
  const [roleId, setRoleId] = useState("");
  const queryClient = useQueryClient();

  const { data: roles, isPending: rolesLoading } = useQuery<Role[], AppError | Error>({
    queryKey: [QUERY_KEYS.GET_PROJECT_ROLES],
    queryFn: getProjectRoles,
    staleTime: QUERY_EXPIRATIONS.GET_PROJECT_ROLES,
    enabled: open,
  });

  const currentRole = roles?.find((r) => r.name === member.role.name);

  // Default the selection to the member's current role once roles have loaded.
  useEffect(() => {
    if (!roleId && currentRole) {
      setRoleId(currentRole.id);
    }
  }, [roleId, currentRole]);

  const mutation = useMutation<string, AppError | Error, UpdateMemberSchema>({
    mutationFn: updateProjectMember,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_PROJECT_MEMBERS, projectId], exact: true });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_PROJECT_BY_ID, projectId], exact: true });
      setOpen(false);
    },
  });

  function handleOpenChange(next: boolean) {
    if (!next) {
      setRoleId("");
      mutation.reset();
    }
    setOpen(next);
  }

  function handleSave() {
    mutation.mutate({ projectId, memberId: member.id, roleId });
  }

  const isUnchanged = !!currentRole && roleId === currentRole.id;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>Change role</DialogTitle>
          <DialogDescription>
            Update the role for{" "}
            <span className="font-semibold text-foreground">
              {member.firstName} {member.lastName}
            </span>
            .
          </DialogDescription>
        </DialogHeader>
        {mutation.isError && <Error title="Unable to change role" error={mutation.error} />}
        {rolesLoading ? <RoleSelectorSkeleton /> : <RoleByIdSelector roles={roles ?? []} value={roleId} onChange={setRoleId} disabled={mutation.isPending} />}
        <DialogFooter showCloseButton>
          <Button onClick={handleSave} disabled={mutation.isPending || rolesLoading || !roleId || isUnchanged}>
            {mutation.isPending ? "Saving..." : "Save changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
